import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  LayoutDashboard, Sparkles, Brain, Heart, Award, 
  BookOpen, Users, Play, Calendar, User, LogOut, 
  Menu, X, ChevronRight, Leaf, Phone, Gift, Shield,
  PenLine, Newspaper, Link2, Store, TreePine, ShoppingBag, CalendarDays, Crown, Lock, Flame, BookMarked
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { hasFullAccess } from "@/components/PremiumGate";

const allNavItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard", free: true },
  { icon: Brain, label: "SMAHOO ChatBOTs", path: "/ai-agents" },
  { icon: Sparkles, label: "Mind-SPA", path: "/mind-spa" },
  { icon: Brain, label: "ME.WELL AI Coach", path: "/ai-coach", free: true },
  { icon: Heart, label: "Mood Tracker", path: "/mood", free: true },
  { icon: Flame, label: "Wellness Journey", path: "/wellness-journey", free: true },
  { icon: Award, label: "SMAHOO Coins", path: "/coins", free: true },
  { icon: Gift, label: "Gift Basket Store", path: "/gift-store" },
  { icon: BookOpen, label: "Life Skills", path: "/life-skills" },
  { icon: Users, label: "Community", path: "/community" },
  { icon: Phone, label: "OnCALL+ Live ICU", path: "/oncall-live" },
  { icon: Play, label: "Basic Streaming", path: "/streaming-basic", free: true },
  { icon: Calendar, label: "Sessions", path: "/sessions" },
  { icon: CalendarDays, label: "Calendar", path: "/calendar" },
  { icon: PenLine, label: "ME.WELL Blog", path: "/blog", free: true },
  { icon: Newspaper, label: "ME.WELL Digest", path: "/digest" },
  { icon: Link2, label: "Affiliate Program", path: "/affiliate" },
  { icon: Store, label: "Wellness Mall", path: "/wellness-mall" },
  { icon: ShoppingBag, label: "Super Store", path: "/super-store" },
  { icon: TreePine, label: "Mind-SPA Retreat", path: "/retreat" },
  { icon: BookMarked, label: "Glossary", path: "/glossary", free: true },
];

export default function DashboardLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-spa-bone flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 w-72 spa-sidebar transform transition-transform duration-300 ease-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full p-6">
          {/* Logo */}
          <div className="flex items-center justify-between mb-8">
            <Link to="/dashboard">
              <img src="/smahoo-logo.jpg" alt="SMAHOO ME.WELL Mind-SPA exPE" className="h-14 w-auto" />
            </Link>
            <button 
              className="lg:hidden p-2 hover:bg-spa-muted rounded-xl"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5 text-spa-moss" />
            </button>
          </div>

          {/* User Info */}
          <div className="flex items-center gap-3 p-4 bg-spa-muted rounded-2xl mb-6">
            <Avatar className="w-10 h-10">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-spa-sage text-white font-serif">
                {user?.name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-spa-moss truncate">{user?.name || "User"}</p>
              <p className="text-xs text-spa-text-secondary capitalize">{user?.subscription_tier || "Free"} Plan</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto space-y-1 -mr-2 pr-2">
            {(() => {
              const isFull = hasFullAccess(user);
              const freeItems = allNavItems.filter(i => i.free);
              const lockedItems = allNavItems.filter(i => !i.free);
              const visibleItems = isFull ? allNavItems : freeItems;

              return (
                <>
                  {visibleItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`spa-nav-item ${isActive ? 'active' : ''}`}
                        onClick={() => setSidebarOpen(false)}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <item.icon className="w-5 h-5" strokeWidth={1.5} />
                        <span>{item.label}</span>
                        {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                      </Link>
                    );
                  })}

                  {/* Free users: show locked items greyed + Upgrade CTA */}
                  {!isFull && (
                    <>
                      <div className="pt-3 pb-1 px-3">
                        <p className="text-[10px] uppercase tracking-widest text-spa-text-secondary/50 font-medium">Premium Features</p>
                      </div>
                      {lockedItems.slice(0, 5).map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="spa-nav-item opacity-40 hover:opacity-60"
                          onClick={() => setSidebarOpen(false)}
                          data-testid={`nav-locked-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          <item.icon className="w-5 h-5" strokeWidth={1.5} />
                          <span>{item.label}</span>
                          <Lock className="w-3.5 h-3.5 ml-auto text-spa-text-secondary" />
                        </Link>
                      ))}
                      <p className="text-[11px] text-spa-text-secondary/50 px-3 py-1">+{lockedItems.length - 5} more features</p>
                      <Link
                        to="/pricing"
                        className="flex items-center gap-3 px-4 py-3 mx-2 mt-2 rounded-2xl bg-gradient-to-r from-amber-50 to-amber-100/50 border border-amber-200/50 text-amber-700 hover:from-amber-100 hover:to-amber-100 transition-all"
                        onClick={() => setSidebarOpen(false)}
                        data-testid="nav-upgrade-premium"
                      >
                        <Crown className="w-5 h-5" strokeWidth={1.5} />
                        <span className="font-medium text-sm">Upgrade to Premium</span>
                      </Link>
                    </>
                  )}
                </>
              );
            })()}
          </nav>

          {/* Bottom Actions */}
          <div className="border-t border-spa-border pt-4 mt-4 space-y-1">
            {["owner", "super_admin", "admin", "moderator", "support"].includes(user?.role) && (
              <Link 
                to="/admin" 
                className="spa-nav-item text-amber-700 hover:bg-amber-50"
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-admin-portal"
              >
                <Shield className="w-5 h-5" strokeWidth={1.5} />
                <span>Admin Portal</span>
              </Link>
            )}
            <Link 
              to="/profile" 
              className={`spa-nav-item ${location.pathname === '/profile' ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
              data-testid="nav-profile"
            >
              <User className="w-5 h-5" strokeWidth={1.5} />
              <span>Profile & Settings</span>
            </Link>
            <button 
              onClick={handleLogout}
              className="spa-nav-item w-full text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" strokeWidth={1.5} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-30 bg-spa-bone/80 backdrop-blur-xl border-b border-spa-border/50 px-4 py-3">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-spa-muted rounded-xl"
              data-testid="mobile-menu-button"
            >
              <Menu className="w-6 h-6 text-spa-moss" />
            </button>
            <Link to="/dashboard">
              <img src="/smahoo-logo.jpg" alt="SMAHOO ME.WELL Mind-SPA exPE" className="h-10 w-auto" />
            </Link>
            <Link to="/profile">
              <Avatar className="w-8 h-8">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-spa-sage text-white font-serif text-sm">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
